import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  MeshBasicMaterial,
  Mesh,
  AudioListener,
  Audio,
  AudioLoader,
  SphereGeometry,
  SRGBColorSpace,
  DirectionalLight,
  TorusGeometry,
  AudioAnalyser,
  CanvasTexture,
  AmbientLight,
} from 'three';
import {
  BlendFunction,
  EffectComposer,
  EffectPass,
  RenderPass,
  SelectiveBloomEffect,
} from 'postprocessing';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export type VisualizerHandle = {
  play: () => void;
  pause: () => void;
  stop: () => void;
  seek: (t: number) => void;
  getCurrentTime: () => number;
  readonly duration: number;
};

export type VisualizerColors = {
  /** Main sphere body (and outer sphere) color. */
  sphere?: string;
  /** Color of the torus rotating around the X-axis. */
  torusX?: string;
  /** Color of the torus rotating around the Y-axis. */
  torusY?: string;
};

const DEFAULT_COLORS = {
  sphere: '#f2c3d2',
  torusX: '#33a328',
  torusY: '#cb006d',
} as const;

export type VisualizerProps = {
  audioLink: string;
  width: number;
  height: number;
  /** Override the sphere/torus colors (e.g. from a brand kit palette). */
  colors?: VisualizerColors;
  onLoad?: (duration: number) => void;
  /** Fires once when audio playback reaches the end naturally (not on user pause/stop). */
  onEnded?: () => void;
};

const Visualizer = forwardRef<VisualizerHandle, VisualizerProps>(function Visualizer(
  { audioLink, width, height, colors, onLoad, onEnded },
  ref,
) {
  const colorsRef = useRef(colors);
  colorsRef.current = colors;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioRef = useRef<Audio<GainNode> | null>(null);
  const durationRef = useRef(0);
  const dimensionRef = useRef({ width, height });
  const rendererRef = useRef<WebGLRenderer | null>(null);
  const cameraRef = useRef<PerspectiveCamera | null>(null);
  const bloomComposerRef = useRef<EffectComposer | null>(null);
  const playStartCtxTimeRef = useRef<number | null>(null);
  const playOffsetRef = useRef(0);
  const lastPositionRef = useRef(0);
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const wasPlayingRef = useRef(false);
  useEffect(() => {
    if (!canvasRef.current) return;
    const { width, height } = dimensionRef.current;
    // Set up scene, camera, and renderer
    const scene = new Scene();
    const camera = new PerspectiveCamera(75, width / height, 0.1, 1000);
    const renderer = new WebGLRenderer({
      canvas: canvasRef.current,
      powerPreference: 'high-performance',
      stencil: false,
      antialias: false,
      depth: false,
    });
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    const ambientLight = new AmbientLight(0xb8b8b8);
    const mainLight = new DirectionalLight(0xffffff, 3);
    mainLight.position.set(-1, 1, 1);

    scene.add(ambientLight, mainLight);
    // Create gradient texture
    const canvasSphere = document.createElement('canvas');
    canvasSphere.width = 128;
    canvasSphere.height = 128;
    const contextSphere = canvasSphere.getContext('2d');
    if (!contextSphere) throw new Error('Could not get 2d context');
    // Create gradient
    const gradientSphere = contextSphere.createRadialGradient(
      canvasSphere.width / 2,
      canvasSphere.height / 2,
      0,
      canvasSphere.width / 2,
      canvasSphere.height / 2,
      canvasSphere.width / 2,
    );

    // Fill canvas with gradient
    contextSphere.fillStyle = gradientSphere;
    contextSphere.fillRect(0, 0, canvasSphere.width, canvasSphere.height);

    const circleGeometry = new SphereGeometry(1.3, 64, 64); // Radius of 5, 64 segments
    const circleMaterial = new MeshBasicMaterial({
      // color: 0x808080, // Pink color
      opacity: 0.15,
      transparent: true,
      depthWrite: false,
      map: new CanvasTexture(canvasSphere),
    });
    const circleMesh = new Mesh(circleGeometry, circleMaterial);

    scene.add(circleMesh);

    renderer.outputColorSpace = SRGBColorSpace;
    const renderScene = new RenderPass(scene, camera);
    const bloomEffect = new SelectiveBloomEffect(scene, camera, {
      blendFunction: BlendFunction.ADD,
      mipmapBlur: true,
      luminanceThreshold: 0.15,
      luminanceSmoothing: 0.3,
      intensity: 4, // To make the bloom bright (0-10)
      radius: 0.5,
    });
    bloomEffect.mipmapBlurPass.radius = 0.5;
    bloomEffect.luminancePass.enabled = true;
    const bloomPass = new EffectPass(camera, bloomEffect);
    bloomEffect.selection.add(circleMesh);
    bloomEffect.inverted = true;
    const bloomComposer = new EffectComposer(renderer, { multisampling: 8 });
    bloomComposer.addPass(renderScene);
    bloomComposer.addPass(bloomPass);

    rendererRef.current = renderer;
    cameraRef.current = camera;
    bloomComposerRef.current = bloomComposer;

    // Handle window resize
    const onWindowResize = () => {
      const { width, height } = dimensionRef.current;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      bloomComposer.setSize(width, height);
    };

    window.addEventListener('resize', onWindowResize);

    // Create the AudioListener and Audio objects
    const audioListener = new AudioListener();
    camera.add(audioListener);
    const sound = new Audio(audioListener);
    audioRef.current = sound;
    lastPositionRef.current = 0;
    playOffsetRef.current = 0;
    playStartCtxTimeRef.current = null;
    durationRef.current = 0;

    // Now, load the audio file and play when ready
    const audioLoader = new AudioLoader();
    audioLoader.load(audioLink, function (buffer) {
      durationRef.current = buffer.duration;
      sound.setBuffer(buffer);
      onLoadRef.current?.(buffer.duration);
    });

    const analyzer = new AudioAnalyser(sound, 32);

    // Resume the audio context if it is suspended (required if we need autoplay on load)
    // const audioContext = audioListener.context;
    // if (audioContext.state === 'suspended') {
    //   audioContext.resume();
    // }

    // Create gradient texture
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const context = canvas.getContext('2d');

    // Create gradient
    if (!context) {
      throw new Error('Could not get 2d context');
    }
    const gradient = context.createRadialGradient(
      canvas.width / 2,
      canvas.height / 2,
      0,
      canvas.width / 2,
      canvas.height / 2,
      canvas.width / 2,
    );

    const sphereColor = colorsRef.current?.sphere ?? DEFAULT_COLORS.sphere;
    const torusXColor = colorsRef.current?.torusX ?? DEFAULT_COLORS.torusX;
    const torusYColor = colorsRef.current?.torusY ?? DEFAULT_COLORS.torusY;

    gradient.addColorStop(0, sphereColor);
    gradient.addColorStop(1, sphereColor);

    // Fill canvas with gradient
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Create a 3D Sphere (SphereGeometry)
    const geometry = new SphereGeometry(1.0, 128, 128); // Radius of 5, 64 segments

    const texture = new CanvasTexture(canvas);
    const material = new MeshBasicMaterial({
      color: sphereColor,
      opacity: 0.6,
      transparent: true,
      map: texture,
    });
    const sphereMesh = new Mesh(geometry, material);
    // Outer sphere is not animated
    const outerSphere = sphereMesh.clone();
    const outerSphereScaleFactor = 1.1;
    outerSphere.scale.set(
      outerSphereScaleFactor,
      outerSphereScaleFactor,
      outerSphereScaleFactor,
    );
    bloomEffect.selection.add(outerSphere);
    scene.add(outerSphere);
    scene.add(sphereMesh);
    // Create a thick ellipse (torus) around the X-axis
    const torusGeometryX = new TorusGeometry(1.2, 0.04, 16, 100); // radius 5, tube thickness 0.2
    const torusMaterialX = new MeshBasicMaterial({
      color: torusXColor,
      opacity: 0.8,
    });
    const torusMeshX = new Mesh(torusGeometryX, torusMaterialX);
    scene.add(torusMeshX);

    // Create a thick ellipse (torus) around the Y-axis
    const torusGeometryY = new TorusGeometry(1.2, 0.04, 16, 100); // radius 5, tube thickness 0.2
    const torusMaterialY = new MeshBasicMaterial({
      color: torusYColor,
    });
    const torusMeshY = new Mesh(torusGeometryY, torusMaterialY);
    scene.add(torusMeshY);

    // Set initial camera position
    camera.position.z = 3;

    // Set up OrbitControls for mouse interaction
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true; // Smooth camera movements
    controls.dampingFactor = 0.25; // Adjust damping speed
    controls.screenSpacePanning = false; // Don't allow panning up/down
    controls.maxPolarAngle = Math.PI / 2; // Prevent camera from going under the sphere

    // Add lighting to the scene

    torusMeshX.rotation.y = 1; // Rotate the green torus around the X-axis
    torusMeshY.rotation.x = 1; // Rotate the green torus around the X-axis

    // Animation loop. Tracked so cleanup can cancel the next frame; without
    // this the rAF keeps firing against disposed Three.js objects after the
    // effect re-runs (e.g. on audioLink change) or the component unmounts.
    let rafId = 0;
    let cancelled = false;
    const animate = () => {
      if (cancelled) return;
      rafId = requestAnimationFrame(animate);

      if (sound.isPlaying && playStartCtxTimeRef.current !== null) {
        const elapsed =
          audioListener.context.currentTime - playStartCtxTimeRef.current;
        const t =
          playOffsetRef.current + elapsed * (sound.playbackRate || 1);
        lastPositionRef.current = durationRef.current
          ? Math.min(t, durationRef.current)
          : t;
      }

      // Natural end-of-playback detection: Three.js Audio's source.onended
      // flips sound.isPlaying to false. We distinguish a natural end from a
      // user pause/stop by checking that playStartCtxTimeRef is still set —
      // our own pause/stop handlers clear it before the transition.
      if (
        wasPlayingRef.current &&
        !sound.isPlaying &&
        playStartCtxTimeRef.current !== null
      ) {
        playStartCtxTimeRef.current = null;
        lastPositionRef.current = durationRef.current || lastPositionRef.current;
        onEndedRef.current?.();
      }
      wasPlayingRef.current = sound.isPlaying;

      const averageFrequency = analyzer.getAverageFrequency();

      // For Sphere animation
      const scaleFactor = 0.8 + averageFrequency / 512;
      sphereMesh.scale.set(scaleFactor, scaleFactor, scaleFactor);
      // For bloomed Circle animation
      // const scaleFactor = 0.65 + averageFrequency / 1024;
      // circleMesh.scale.set(scaleFactor, scaleFactor, scaleFactor);

      // Sinusoidal movement for the green torus (move from bottom to top)
      // torusMeshX.position.y = 1.2 * Math.sin(time); // Moves between -2 and +2 on the Y-axis

      // Rotate the green torus around the X-axis
      torusMeshX.rotation.x += 0.001; // Rotate around the X-axis

      // Rotate the pink torus around the Y-axis
      torusMeshY.rotation.y += 0.0015; // Rotate around the Y-axis

      sphereMesh.rotation.z += 0.01;
      // Selection is inverted, so we add meshes that should not be affected by the effect here
      bloomEffect.selection.add(torusMeshX);
      bloomEffect.selection.add(torusMeshY);
      bloomEffect.ignoreBackground = true;

      // Update the controls (for smooth movement)
      controls.update();

      // Render the scene from the camera's perspective
      // renderer.render(scene, camera);
      renderer.setPixelRatio(window.devicePixelRatio);
      bloomComposer.render();
    };

    rafId = requestAnimationFrame(animate);

    // Cleanup on component unmount
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onWindowResize);
      rendererRef.current = null;
      cameraRef.current = null;
      bloomComposerRef.current = null;
      renderer.dispose();
      bloomComposer.dispose();
      sphereMesh.geometry.dispose();
      sphereMesh.material.dispose();
      torusMeshX.geometry.dispose();
      torusMeshX.material.dispose();
      torusMeshY.geometry.dispose();
      torusMeshY.material.dispose();
      circleMesh.geometry.dispose();
      circleMesh.material.dispose();
      // outerSphere shares sphereMesh's geometry and material via Mesh.clone() —
      // disposing them once on sphereMesh is enough.
    };
  }, [audioLink]);

  useEffect(() => {
    dimensionRef.current = { width, height };
    rendererRef.current?.setSize(width, height);
    if (cameraRef.current) {
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
    }
    bloomComposerRef.current?.setSize(width, height);
  }, [width, height]);

  useImperativeHandle(ref, () => {
    const clampToDuration = (t: number) => {
      if (t < 0) return 0;
      return durationRef.current ? Math.min(t, durationRef.current) : t;
    };

    return {
      play: () => {
        const sound = audioRef.current;
        if (!sound || !sound.buffer || sound.isPlaying) return;
        sound.offset = lastPositionRef.current;
        playOffsetRef.current = lastPositionRef.current;
        playStartCtxTimeRef.current = sound.context.currentTime;
        sound.play();
      },
      pause: () => {
        const sound = audioRef.current;
        if (!sound || !sound.isPlaying) return;
        if (playStartCtxTimeRef.current !== null) {
          const elapsed =
            sound.context.currentTime - playStartCtxTimeRef.current;
          lastPositionRef.current = clampToDuration(
            playOffsetRef.current + elapsed * (sound.playbackRate || 1),
          );
        }
        sound.stop();
        playStartCtxTimeRef.current = null;
      },
      stop: () => {
        const sound = audioRef.current;
        if (sound?.isPlaying) sound.stop();
        lastPositionRef.current = 0;
        playOffsetRef.current = 0;
        playStartCtxTimeRef.current = null;
      },
      seek: (t: number) => {
        const sound = audioRef.current;
        if (!sound) return;
        const safeT = clampToDuration(t);
        const wasPlaying = sound.isPlaying;
        if (wasPlaying) sound.stop();
        sound.offset = safeT;
        lastPositionRef.current = safeT;
        playOffsetRef.current = safeT;
        if (wasPlaying && sound.buffer) {
          playStartCtxTimeRef.current = sound.context.currentTime;
          sound.play();
        } else {
          playStartCtxTimeRef.current = null;
        }
      },
      getCurrentTime: () => {
        const sound = audioRef.current;
        if (
          !sound ||
          !sound.isPlaying ||
          playStartCtxTimeRef.current === null
        ) {
          return lastPositionRef.current;
        }
        const elapsed =
          sound.context.currentTime - playStartCtxTimeRef.current;
        return clampToDuration(
          playOffsetRef.current + elapsed * (sound.playbackRate || 1),
        );
      },
      get duration() {
        return durationRef.current;
      },
    };
  }, []);

  return (
    <div style={{ position: 'relative', width, height }}>
      <canvas
        ref={canvasRef}
        style={{
          pointerEvents: 'none',
          width,
          height,
        }}
      />
    </div>
  );
});

export default Visualizer;
